/**
 * ScanOverlay — the live 3D-scan / triangulation visual.
 *
 * Reads the spatial backend's tracked feature points (VO or AlvaAR) and draws
 * them as glowing nodes wired into a shifting triangulation mesh — the system
 * visibly "mapping" the space it is colonising. Cheap: nearest-neighbour edges
 * rather than a full Delaunay, which reads the same at this density.
 *
 * If the active backend exposes no feature points (orientation-only), it simply
 * draws nothing.
 *
 * ── Deliberately half-noticed ─────────────────────────────────────────────
 * This used to be a bright teal wireframe at up to 0.5 alpha, which read as a
 * technical HUD bolted onto an artwork — and worse, it fought the gold in every
 * icon, so the two visual systems never looked like one thing.
 *
 * It is now dim ochre at roughly a fifth of that. The mesh should register at
 * the edge of vision as something the room is doing, not as an instrument
 * reading. If you find yourself looking AT it rather than through it, it is
 * still too strong.
 *
 * Ochre, not gold: gold is reserved for high-confidence tracking points and the
 * Eye (see `Iconostasis/pigments.ts`). A gold mesh would destroy that signal.
 */

import { useEffect, useRef } from 'react';
import { runtime } from '../session/runtime.js';
import { useStore } from '../state/store.js';
import { PIGMENT, hexToRgb } from './Iconostasis/pigments.js';

/** Max normalised distance to connect two points. Shorter than before, so the
 *  mesh reads as local structure rather than a net thrown over the whole frame. */
const MAX_EDGE = 0.11;
const NEIGHBOURS = 2;
/** Ceiling on line alpha. Was effectively 0.35; this is the "half-noticed" mark. */
const LINE_ALPHA = 0.085;
/** Ceiling on node alpha. Nodes carry a little more than lines, but not much. */
const NODE_ALPHA = 0.2;

const OCHRE = hexToRgb(PIGMENT.ochre).map((c) => Math.round(c * 255));
const LEAD = hexToRgb(PIGMENT.leadWhite).map((c) => Math.round(c * 255));

export function ScanOverlay() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const show = useStore((s) => s.showScan);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let raf = 0;
    let t = 0;
    const loop = () => {
      t += 1;
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      const pts = runtime.spatial.featurePoints?.() ?? [];
      if (pts.length > 2) {
        const conf = runtime.spatial.confidence?.() ?? 1;
        // Confidence still modulates it, but across a much narrower band — the
        // mesh should not brighten into a HUD when tracking happens to be good.
        const alpha = 0.45 + conf * 0.55;

        // Edges: connect each point to its nearest few within MAX_EDGE.
        ctx.lineWidth = 1;
        for (let i = 0; i < pts.length; i++) {
          const a = pts[i]!;
          const dists: { j: number; d: number }[] = [];
          for (let j = 0; j < pts.length; j++) {
            if (i === j) continue;
            const b = pts[j]!;
            const d = Math.hypot(a.x - b.x, a.y - b.y);
            if (d < MAX_EDGE) dists.push({ j, d });
          }
          dists.sort((p, q) => p.d - q.d);
          for (const { j, d } of dists.slice(0, NEIGHBOURS)) {
            const b = pts[j]!;
            const fade = (1 - d / MAX_EDGE) * alpha * LINE_ALPHA;
            ctx.strokeStyle = `rgba(${OCHRE[0]},${OCHRE[1]},${OCHRE[2]},${fade.toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(a.x * W, a.y * H);
            ctx.lineTo(b.x * W, b.y * H);
            ctx.stroke();
          }
        }

        // Nodes. The twinkle is gone: a pulsing mesh draws the eye, and this is
        // meant to be seen without being looked at. A fixed dim mark reads as
        // structure the room already had.
        for (let i = 0; i < pts.length; i++) {
          const p = pts[i]!;
          const x = p.x * W;
          const y = p.y * H;
          const tw = 1;
          ctx.fillStyle = `rgba(${OCHRE[0]},${OCHRE[1]},${OCHRE[2]},${(alpha * NODE_ALPHA).toFixed(3)})`;
          ctx.fillRect(x - 1, y - 1, 2, 2);
          ctx.fillStyle = `rgba(${LEAD[0]},${LEAD[1]},${LEAD[2]},${(alpha * NODE_ALPHA * 0.4 * tw).toFixed(3)})`;
          ctx.fillRect(x - 0.5, y - 0.5, 1, 1);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="scan-overlay" style={{ display: show ? 'block' : 'none' }} />;
}
